import React, { useState } from 'react';
import { useSelectedRestaurant } from '@/hooks/useSelectedRestaurant';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui';
import { Badge } from '@/components/ui';
import { Button } from '@/components/ui';
import { Progress } from '@/components/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import {
  getBacklinkOpportunities,
  getReputationChecklist,
  getBacklinkMetrics
} from '@/data/mocks/backlinks';
import { restaurants, getRestaurantById } from '@/data/mocks/restaurants';
import {
  Link as LinkIcon,
  Star,
  CheckCircle,
  Clock,
  AlertCircle,
  ExternalLink,
  Mail,
  MessageCircle,
  Building2,
  TrendingUp,
  Target,
  Users
} from 'lucide-react';

export const BacklinksPage: React.FC = () => {
  const { selectedRestaurant, setSelectedRestaurant } = useSelectedRestaurant();
  const [selectedLanguage, setSelectedLanguage] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  const currentRestaurant = getRestaurantById(selectedRestaurant);
  const opportunities = getBacklinkOpportunities(selectedRestaurant);
  const reputationItems = getReputationChecklist(selectedRestaurant);
  const stats = getBacklinkMetrics(selectedRestaurant);

  // Filtrage
  const filteredOpportunities = opportunities.filter(opp => {
    const languageMatch = selectedLanguage === 'all' || opp.language === selectedLanguage;
    const statusMatch = selectedStatus === 'all' || opp.status === selectedStatus;
    return languageMatch && statusMatch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live':
        return 'success';
      case 'secured':
        return 'success';
      case 'negotiating':
        return 'warning';
      case 'contacted':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getRelevanceColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'default';
  };

  return (
    <div className="space-y-8">
      {/* En-tête avec sélecteur d'établissement */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Backlinks & E-réputation</h1>
          <p className="text-gray-600 mt-1">Opportunités de liens et gestion de la réputation</p>
        </div>
        <div className="flex gap-2">
          {Object.values(restaurants).map((restaurant) => (
            <Button
              key={restaurant.id}
              variant={selectedRestaurant === restaurant.id ? "default" : "outline"}
              onClick={() => setSelectedRestaurant(restaurant.id)}
              className="flex items-center gap-2"
            >
              <Building2 className="w-4 h-4" />
              {restaurant.name}
            </Button>
          ))}
        </div>
      </motion.div>

      {/* Statistiques réelles SEMrush */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-5 gap-6"
      >
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">Total Backlinks</p>
                <p className="text-2xl font-bold">{stats.stats.total.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <LinkIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-600">
              Follow: {stats.stats.follow} / Nofollow: {stats.stats.nofollow}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">Domaines Référents</p>
                <p className="text-2xl font-bold">{stats.stats.referringDomains}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Building2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-600">
              IPs: {stats.stats.referringIPs}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">DA Moyen</p>
                <p className="text-2xl font-bold">{stats.averageMetrics.da}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">Opportunités</p>
                <p className="text-2xl font-bold">{stats.totalOpportunities}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Target className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">E-réputation</p>
                <p className="text-2xl font-bold">
                  {stats.reputation.completed}/{stats.reputation.total}
                </p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <Star className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Top Anchors (données réelles SEMrush) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        <Card>
          <CardHeader>
            <CardTitle>Top Ancres de Backlinks</CardTitle>
            <CardDescription>
              Distribution des ancres (données SEMrush)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.stats.topAnchors.map((anchor, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{anchor.anchor}</span>
                      <Badge variant="outline">{anchor.count} backlinks</Badge>
                    </div>
                    <Progress value={anchor.percentage * 10} className="mt-2" />
                  </div>
                  <div className="ml-4 text-right">
                    <span className="text-sm font-semibold">{anchor.percentage}%</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Filtres */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4 items-center">
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
              >
                <option value="all">Toutes langues</option>
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="pt">Português</option>
              </select>

              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
              >
                <option value="all">Tous statuts</option>
                <option value="to_contact">À contacter</option>
                <option value="contacted">Contacté</option>
                <option value="negotiating">Négociation</option>
                <option value="secured">Sécurisé</option>
                <option value="live">Live</option>
              </select>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Opportunités de backlinks */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card>
          <CardHeader>
            <CardTitle>Opportunités de Backlinks</CardTitle>
            <CardDescription>
              {filteredOpportunities.length} opportunités trouvées
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Langue</TableHead>
                    <TableHead>DA/DR</TableHead>
                    <TableHead>Relevance</TableHead>
                    <TableHead>Angle de Contact</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOpportunities.map((opp) => (
                    <TableRow key={opp.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{opp.name}</div>
                          <a
                            href={opp.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-gray-500 hover:text-blue-600 flex items-center gap-1"
                          >
                            {opp.url}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{opp.type}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{opp.language.toUpperCase()}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="text-sm">DA: {opp.metrics.da}</div>
                          <div className="text-sm text-gray-500">DR: {opp.metrics.dr}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getRelevanceColor(opp.metrics.relevance)}>
                          {opp.metrics.relevance}%
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          <p className="text-sm line-clamp-2">{opp.contact.angle}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {opp.contact.method === 'email' ? (
                              <Mail className="w-3 h-3 text-gray-400" />
                            ) : (
                              <MessageCircle className="w-3 h-3 text-gray-400" />
                            )}
                            <span className="text-xs text-gray-500">
                              {opp.contact.email || opp.contact.social}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusColor(opp.status)}>
                          {opp.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm">
                            Contacter
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Checklist E-réputation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card>
          <CardHeader>
            <CardTitle>Checklist E-réputation</CardTitle>
            <CardDescription>
              Gestion des avis et présence en ligne
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {reputationItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`p-2 rounded-full ${
                        item.status === 'done'
                          ? 'bg-green-100'
                          : item.status === 'in_progress'
                          ? 'bg-blue-100'
                          : 'bg-gray-100'
                      }`}
                    >
                      {item.status === 'done' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : item.status === 'in_progress' ? (
                        <Clock className="w-5 h-5 text-blue-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-gray-600" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium">{item.platform}</h4>
                      <p className="text-sm text-gray-600">{item.type}</p>
                      {item.notes && (
                        <p className="text-sm text-gray-500 mt-1">{item.notes}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right mr-4">
                      <div className="text-sm font-medium">Impact</div>
                      <Progress value={item.impact} className="w-24 mt-1" />
                    </div>
                    <Badge
                      variant={
                        item.priority === 'high'
                          ? 'destructive'
                          : item.priority === 'medium'
                          ? 'warning'
                          : 'default'
                      }
                    >
                      {item.priority}
                    </Badge>
                    <Badge
                      variant={
                        item.status === 'done'
                          ? 'success'
                          : item.status === 'in_progress'
                          ? 'warning'
                          : 'default'
                      }
                    >
                      {item.status}
                    </Badge>
                    <Button variant="outline" size="sm">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};